import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Play, Clock, Users, MapPin, FileText, Camera, 
  BarChart3, CheckCircle, Shield, Settings
} from 'lucide-react';

const Demo = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Clock,
      title: '1. Time Tracking',
      description: 'Workers clock in/out with GPS verification and wellness checks. Auto-timeout after 8 hours ensures compliance.',
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800'
    },
    {
      icon: MapPin,
      title: '2. Location Verification',
      description: 'Admins enter job site addresses. Workers must be within 500m of the location to start work. Live map view shows all active workers.',
      image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800'
    },
    {
      icon: Users,
      title: '3. Job Management',
      description: 'Assign jobs to workers, require photo documentation, track completion status. Workers get email/SMS notifications.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800'
    },
    {
      icon: Camera,
      title: '4. Photo Documentation',
      description: 'Require workers to upload job completion photos. Show customers the finished work. Up to 10 photos per job.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'
    },
    {
      icon: BarChart3,
      title: '5. Advanced Reports',
      description: 'Generate payroll reports, user activity analytics, job statistics. Edit time entries with full audit trail. Export to CSV.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800'
    },
    {
      icon: Shield,
      title: '6. Admin Controls',
      description: 'Multiple administrators, shift duration settings, bulk time editing, team management. Complete control over your workforce.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="mr-4"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                Hines Time
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/signin')}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-green-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">See Hines Time in Action</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Discover how Hines Time helps businesses track time, manage jobs, and process payroll in minutes instead of hours.
          </p>
        </div>
      </div>

      {/* Video Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-2xl">
            <CardContent className="p-0">
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg overflow-hidden aspect-video flex items-center justify-center">
                <div className="text-center text-white p-12">
                  <Play className="w-24 h-24 mx-auto mb-6 text-blue-400" />
                  <h3 className="text-2xl font-bold mb-4">Interactive Demo Coming Soon</h3>
                  <p className="text-gray-300 mb-8">
                    We're creating an interactive walkthrough to show you all the features.
                    In the meantime, start your free trial to explore the platform yourself!
                  </p>
                  <Button
                    onClick={() => navigate('/register')}
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Start Free Trial Instead
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Feature Walkthrough */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Complete Walkthrough
            </h2>
            <p className="text-xl text-gray-600">
              Here's everything you can do with Hines Time
            </p>
          </div>

          <div className="space-y-20">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              const isEven = idx % 2 === 0;
              
              return (
                <div key={idx} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  {isEven ? (
                    <>
                      <div>
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-3xl font-bold text-gray-900">{feature.title}</h3>
                        </div>
                        <p className="text-lg text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                      <div className="relative">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="rounded-xl shadow-2xl border-4 border-blue-100"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="relative lg:order-2">
                        <div>
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900">{feature.title}</h3>
                          </div>
                          <p className="text-lg text-gray-600 leading-relaxed">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                      <div className="relative lg:order-1">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="rounded-xl shadow-2xl border-4 border-green-100"
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Try Hines Time free for 30 days. No commitments, cancel anytime.
          </p>
          <Button
            onClick={() => navigate('/register')}
            size="lg"
            className="bg-white text-blue-600 hover:bg-blue-50 text-xl h-16 px-12"
          >
            Start Your Free Trial
          </Button>
          <p className="text-sm text-blue-100 mt-4">
            Credit card required • $9.99 setup + $5.99/user/month after trial
          </p>
        </div>
      </div>
    </div>
  );
};

export default Demo;
